import { getSupabase } from '../supabaseClient';
import type { TodoTagEntity } from '../../types/data';
import { fetchAllPages } from './pagination';
import type { SyncCursor } from './syncCursor';

const TABLE_TAGS = 'todo_tags';

const stripAuditFields = <T extends { created_at?: unknown; modified_at?: unknown }>(input: T): Omit<T, 'created_at' | 'modified_at'> => {
  const { created_at, modified_at, ...rest } = input;
  return rest;
};

export const tagRepo = {
  async listTags(): Promise<TodoTagEntity[]> {
    const supabase = getSupabase();
    try {
      return await fetchAllPages<TodoTagEntity>(() =>
        supabase
          .from(TABLE_TAGS)
          .select('*')
          .order('sort_order', { ascending: true })
          .order('created_at', { ascending: true })
          .order('id', { ascending: true })
      );
    } catch {
      // Backward-compatible fallback for environments where sort_order is not deployed yet.
    }

    return await fetchAllPages<TodoTagEntity>(() =>
      supabase
        .from(TABLE_TAGS)
        .select('*')
        .order('created_at', { ascending: true })
        .order('id', { ascending: true })
    );
  },

  async listTagsModifiedAfter(cursor: SyncCursor): Promise<TodoTagEntity[]> {
    const supabase = getSupabase();
    return await fetchAllPages<TodoTagEntity>(() =>
      applyModifiedAtIdCursor(
        supabase
        .from(TABLE_TAGS)
        .select('*'),
        cursor
      )
        .order('modified_at', { ascending: true })
        .order('id', { ascending: true })
    );
  },

  async createTag(input: Omit<TodoTagEntity, 'id' | 'created_at' | 'modified_at' | 'is_deleted'> & { id?: string }): Promise<TodoTagEntity> {
    const supabase = getSupabase();
    const row = {
      title: input.title,
      body: input.body,
      is_deleted: false,
      sort_order: input.sort_order,
    };
    const { data, error } = await supabase
      .from(TABLE_TAGS)
      .insert(row as any)
      .select()
      .single();
    if (error) throw error;
    return data as TodoTagEntity;
  },

  async updateTag(id: string, patch: Partial<TodoTagEntity>): Promise<TodoTagEntity | null> {
    const supabase = getSupabase();
    const toUpdate = stripAuditFields(patch);
    if (Object.keys(toUpdate).length === 0) return null;
    const { data, error } = await supabase
      .from(TABLE_TAGS)
      .update(toUpdate)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as TodoTagEntity;
  },

  async softDeleteTag(id: string): Promise<void> {
    const supabase = getSupabase();
    const { error } = await supabase
      .from(TABLE_TAGS)
      .update({ is_deleted: true })
      .eq('id', id);
    if (error) throw error;
  },
};

const applyModifiedAtIdCursor = (query: any, cursor: SyncCursor): any => {
  const id = cursor.tieBreaker[0];
  if (!id) return query.gte('modified_at', cursor.modifiedAt);

  return query.or(`modified_at.gt.${cursor.modifiedAt},and(modified_at.eq.${cursor.modifiedAt},id.gt.${id})`);
};
